import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { List, ListItem } from 'chaoskit/src/components'

import { StylesHeaderVariables } from '~partials/header/UI'
import { stickyIt } from '~styles/utility'

const NavTableOfContentsLink = ({ active, ...rest }) => {
  const theme = useTheme()

  return (
    <a
      css={[
        {
          position: 'relative',
          color: theme.fontColor.muted,
          transition: `color ${theme.timing.base} ${theme.transition.base}`,

          '&:hover, &:focus': {
            color: theme.fontColor.base,
          },

          '&::before': {
            content: "''",
            position: 'absolute',
            width: theme.space.xsmall,
            height: theme.space.xsmall,
            left: -theme.space.small + -theme.space.xsmall / 2,
            top: theme.space.small,
            borderRadius: 9999,
            background: theme.color.primary.base,
            opacity: 0,
            transform: 'scale(0)',
            transition: `transform ${theme.timing.base} ${theme.transition.base}, opacity ${theme.timing.base} ${theme.transition.base}`,
          },
        },

        active && {
          color: theme.fontColor.base,

          '&::before': {
            opacity: 1,
            transform: 'scale(1)',
          },
        },
      ]}
      {...rest}
    />
  )
}

NavTableOfContentsLink.propTypes = {
  active: PropTypes.bool,
}

const TableOfContents = ({
  tree,
  hasStickyHeader = true,
  stickyBp = 'medium',
  ...rest
}) => {
  const theme = useTheme()

  const [activeNavItem, setActiveNavItem] = useState()

  useEffect(() => {
    const $headings = Array.from(document.querySelectorAll('.AutoLinkHeader'))

    const navItems = $headings.reduce((acc, curr) => acc.concat(curr.id), [])

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && navItems.includes(entry.target.id)) {
            setActiveNavItem(entry.target.id)
          }
        })
      },
      { threshold: 1 }
    )

    $headings.forEach((section) => {
      observer.observe(section)
    })

    // Cleanup on unmount
    return () => {
      if (observer) observer.disconnect()
    }
  }, [])

  return (
    <nav
      css={[
        {
          borderRadius: theme.borderRadius.base,
          padding: theme.space.base,
          background: theme.color.panel.base,
          alignSelf: 'start',
          marginBottom: theme.space.base,
          overflow: 'auto',
          maxHeight: `calc(100vh - ${
            StylesHeaderVariables(theme).height + theme.space.base
          }px)`,
        },

        stickyBp === 'medium' && {
          [theme.mq.medium]: [
            {
              marginBottom: 0,
            },

            hasStickyHeader && {
              ...stickyIt(theme),
            },

            !hasStickyHeader && {
              position: 'sticky',
              top: theme.space.base,
            },
          ],
        },

        stickyBp === 'large' && {
          [theme.mq.large]: [
            {
              marginBottom: 0,
            },

            hasStickyHeader && {
              ...stickyIt(theme),
            },

            !hasStickyHeader && {
              position: 'sticky',
              top: theme.space.base,
            },
          ],
        },

        stickyBp === 'xlarge' && {
          [theme.mq.xlarge]: [
            {
              marginBottom: 0,
            },

            hasStickyHeader && {
              ...stickyIt(theme),
            },

            !hasStickyHeader && {
              position: 'sticky',
              top: theme.space.base,
            },
          ],
        },
      ]}
      {...rest}
    >
      <List space="xsmall" css={{ marginBottom: 0, ...theme.text.small }}>
        {tree.items.map((branch) => (
          <ListItem key={branch.url}>
            <NavTableOfContentsLink
              active={activeNavItem === branch?.url?.replace('#', '')}
              href={branch.url}
              key={branch.url}
            >
              {branch.title}
            </NavTableOfContentsLink>
            {branch.items && (
              <List
                space="xsmall"
                css={{
                  marginTop: `${theme.space.xsmall}px !important`,
                }}
              >
                {branch.items.map((twig) => (
                  <ListItem key={twig.url}>
                    <NavTableOfContentsLink
                      active={activeNavItem === twig?.url?.replace('#', '')}
                      href={twig.url}
                    >
                      {twig.title}
                    </NavTableOfContentsLink>
                  </ListItem>
                ))}
              </List>
            )}
          </ListItem>
        ))}
      </List>
    </nav>
  )
}

TableOfContents.propTypes = {
  tree: PropTypes.object.isRequired,
  hasStickyHeader: PropTypes.bool,
  stickyBp: PropTypes.oneOf(['medium', 'large', 'xlarge']),
}

export default TableOfContents
